import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Collapse, Divider, Paper, Typography } from '@mui/material';
import { Chat as ChatIcon } from '@mui/icons-material';
import { SessionStateDTO } from '../../services/studentSessionService';
import { getStudentContent, TutorTopicContentDTO } from '../../services/tutorTopicContentService';
import StudentGnPanel, { StudentGnPanelRef } from '../gn/StudentGnPanel';
import ChatPanel from '../aicanvas/ChatPanel';

interface PlaygroundContentProps {
  sessionState: SessionStateDTO | null;
  courseId: number;
}

/**
 * The live-session view for a student (spec 041/042 base, spec 046 GN integration): the
 * tutor's published canvas renders inside the GN's read-only region, refreshed whenever
 * the session seq bumps (a publish, spec 046 SC-012) — this component no longer renders
 * the canvas directly, nor its own paste-a-question popover, since StudentGnPanel now
 * owns the unified cue/voice/check-answer pipeline for every paste (FR-012).
 */
const PlaygroundContent: React.FC<PlaygroundContentProps> = ({ sessionState, courseId }) => {
  const [topicContent, setTopicContent] = useState<TutorTopicContentDTO | null>(null);
  const [chatOpen, setChatOpen] = useState(false);

  const nodeId = sessionState?.currentTopicId ?? 0;
  const personalCanvasRef = useRef<StudentGnPanelRef>(null);

  // Base (non-canvas) content only — the published tutor canvas renders inside the GN's
  // read-only region, refetched via publishRefreshKey on each seq bump (spec 046 SC-012).
  useEffect(() => {
    if (!sessionState?.currentTopicId) {
      setTopicContent(null);
      return;
    }
    getStudentContent(courseId, sessionState.currentTopicId)
      .then((dto) => setTopicContent(dto))
      .catch(() => setTopicContent(null));
  }, [courseId, sessionState?.currentTopicId, sessionState?.currentPageIndex, sessionState?.lastSeq]);

  if (!sessionState) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" p={4}>
        <Typography color="text.secondary">No active session.</Typography>
      </Box>
    );
  }

  if (sessionState.status === 'ENDED') {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" p={4}>
        <Typography color="text.secondary">Session has ended.</Typography>
      </Box>
    );
  }

  if (!sessionState.currentTopicId) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" p={4}>
        <Typography color="text.secondary">Waiting for tutor to select a topic…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Base (non-canvas) topic content, when the topic has any. The tutor's PUBLISHED
          canvas renders inside the GN's read-only region below (spec 046 FR-035/FR-038). */}
      {topicContent?.format !== 'rich_text' && topicContent?.content && (
        <Box sx={{ flex: '0 0 auto', maxHeight: '35%', overflow: 'auto', p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box dangerouslySetInnerHTML={{ __html: topicContent.content }} />
        </Box>
      )}

      {/* Guruji NoteBook — the student's private notebook; tutor's published content is the
          read-only region inside it, refreshed on every publish (seq bump). */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Guruji NoteBook</Typography>
          <Button size="small" startIcon={<ChatIcon />} onClick={() => setChatOpen((v) => !v)} variant={chatOpen ? 'contained' : 'outlined'}>
            Ask AI
          </Button>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <StudentGnPanel
              ref={personalCanvasRef}
              courseId={courseId}
              nodeId={nodeId}
              publishRefreshKey={sessionState?.lastSeq ?? 0}
            />
          </Box>
          <Collapse in={chatOpen} orientation="horizontal">
            <Paper elevation={0} sx={{ width: 300, height: '100%', borderLeft: '1px solid', borderColor: 'divider' }}>
              <ChatPanel role="student" courseId={courseId} nodeId={nodeId} />
            </Paper>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
};

export default PlaygroundContent;
